import React, { useCallback, useMemo, useRef, useState } from 'react';

import Papa from 'papaparse';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import UsersService from '~/services/UsersService';

type CsvRow = {
  PersonID: string;
  Name: string;
  SupervisorID: string;
  Email: string;
};

type TreeNode = {
  PersonID: number;
  Name: string;
  Email: string;
  SupervisorID: number;
  children: Array<TreeNode>;
};

type CheckedState = Map<number, boolean>;

const DEFAULT_HEAD_EMAIL = 'rob.rossi@luxuryescapes.com';

function buildTree(rows: Array<CsvRow>, headEmail: string): { root: TreeNode | null; error: string | null } {
  const byId = new Map<number, CsvRow>();
  const childrenOf = new Map<number, Array<number>>();

  for (const row of rows) {
    const pid = parseInt(row.PersonID, 10);
    if (isNaN(pid)) continue;
    byId.set(pid, row);
    const sid = parseInt(row.SupervisorID, 10);
    if (!isNaN(sid)) {
      const existing = childrenOf.get(sid) || [];
      existing.push(pid);
      childrenOf.set(sid, existing);
    }
  }

  const normalised = headEmail.trim().toLowerCase();
  let headRow: CsvRow | undefined;
  for (const row of byId.values()) {
    if (row.Email && row.Email.trim().toLowerCase() === normalised) {
      headRow = row;
      break;
    }
  }

  if (!headRow) {
    return { root: null, error: `Could not find employee with email "${headEmail}" in the CSV.` };
  }

  function buildNode(pid: number): TreeNode {
    const row = byId.get(pid)!;
    const kids = childrenOf.get(pid) || [];
    return {
      PersonID: pid,
      Name: row.Name,
      Email: row.Email || '',
      SupervisorID: parseInt(row.SupervisorID, 10) || 0,
      children: kids.map(buildNode).sort((a, b) => a.Name.localeCompare(b.Name)),
    };
  }

  const headPid = parseInt(headRow.PersonID, 10);
  const root = buildNode(headPid);
  return { root, error: null };
}

function collectAllIds(node: TreeNode): Array<number> {
  const ids: Array<number> = [node.PersonID];
  for (const child of node.children) {
    ids.push(...collectAllIds(child));
  }
  return ids;
}

function countChecked(node: TreeNode, checked: CheckedState): number {
  let count = checked.get(node.PersonID) ? 1 : 0;
  for (const child of node.children) {
    count += countChecked(child, checked);
  }
  return count;
}

type AgentIdMap = Map<string, string>; // lowercase email -> agentId (UUID)

function generateOutputJson(root: TreeNode, checked: CheckedState, agentIds: AgentIdMap): string {
  type OutputAgent = {
    PersonID: number;
    Name: string;
    Email: string;
    SupervisorID: number;
    agentId?: string;
    subordinates?: Array<OutputAgent>;
  };

  function toOutputAgent(node: TreeNode): OutputAgent {
    const agent: OutputAgent = {
      PersonID: node.PersonID,
      Name: node.Name,
      Email: node.Email,
      SupervisorID: node.SupervisorID,
    };
    const id = node.Email ? agentIds.get(node.Email.toLowerCase()) : undefined;
    if (id) agent.agentId = id;
    return agent;
  }

  function flattenCheckedDescendants(node: TreeNode): Array<OutputAgent> {
    const result: Array<OutputAgent> = [];
    for (const child of node.children) {
      if (checked.get(child.PersonID)) {
        result.push(toOutputAgent(child));
      }
      result.push(...flattenCheckedDescendants(child));
    }
    return result;
  }

  const topLevel: Array<OutputAgent> = [];
  for (const teamLeader of root.children) {
    if (!checked.get(teamLeader.PersonID)) continue;
    const subordinates = flattenCheckedDescendants(teamLeader);
    const leader = toOutputAgent(teamLeader);
    leader.subordinates = subordinates;
    topLevel.push(leader);
  }

  return JSON.stringify(topLevel, null, 2);
}

function collectAllEmails(node: TreeNode, checked: CheckedState): Array<string> {
  const emails: Array<string> = [];
  if (checked.get(node.PersonID) && node.Email) {
    emails.push(node.Email);
  }
  for (const child of node.children) {
    emails.push(...collectAllEmails(child, checked));
  }
  return emails;
}

async function lookupAgentIds(
  emails: Array<string>,
  brand: string,
  onProgress: (done: number, total: number) => void,
): Promise<{ agentIds: AgentIdMap; missing: Array<string> }> {
  const unique = [...new Set(emails.map((e) => e.toLowerCase()))];
  const agentIds: AgentIdMap = new Map();
  const missing: Array<string> = [];
  const CONCURRENCY = 5;

  for (let i = 0; i < unique.length; i += CONCURRENCY) {
    const batch = unique.slice(i, i + CONCURRENCY);
    const results = await Promise.allSettled(
      batch.map(async (email) => {
        const users = await UsersService.getUsers({ filter: email, limit: 1, brand });
        const match = Array.isArray(users)
          ? users.find((u: { email?: string }) => u.email?.toLowerCase() === email)
          : null;
        return { email, id: match?.id_member || null };
      }),
    );
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value.id) {
        agentIds.set(result.value.email, result.value.id);
      } else {
        const email = result.status === 'fulfilled' ? result.value.email : '';
        if (email) missing.push(email);
      }
    }
    onProgress(Math.min(i + CONCURRENCY, unique.length), unique.length);
  }

  return { agentIds, missing };
}

function HierarchyNode({
  node,
  checked,
  onToggle,
  depth,
}: {
  node: TreeNode;
  checked: CheckedState;
  onToggle: (pid: number, newVal: boolean) => void;
  depth: number;
}) {
  const isChecked = checked.get(node.PersonID) ?? false;
  const hasChildren = node.children.length > 0;

  return (
    <Box sx={{ pl: depth > 0 ? 3 : 0 }}>
      <Box display="flex" alignItems="center" sx={{ py: 0.25 }}>
        <Checkbox
          size="small"
          checked={isChecked}
          onChange={(e) => onToggle(node.PersonID, e.target.checked)}
          sx={{ p: 0.5 }}
        />
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          <strong>{node.Name}</strong>
          {node.Email && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {node.Email}
            </Typography>
          )}
          {hasChildren && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({node.children.length} reports)
            </Typography>
          )}
        </Typography>
      </Box>
      {node.children.map((child) => (
        <HierarchyNode key={child.PersonID} node={child} checked={checked} onToggle={onToggle} depth={depth + 1} />
      ))}
    </Box>
  );
}

export default function UpdateCsAgentsPage() {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const [headEmail, setHeadEmail] = useState(DEFAULT_HEAD_EMAIL);
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [checked, setChecked] = useState<CheckedState>(new Map());
  const [outputJson, setOutputJson] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lookupProgress, setLookupProgress] = useState<{ done: number; total: number } | null>(null);
  const [missingAgentIds, setMissingAgentIds] = useState<Array<string>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setOutputJson(null);
      setParseError(null);

      Papa.parse<CsvRow>(file, {
        header: true,
        skipEmptyLines: true,
        complete(results) {
          const { root, error } = buildTree(results.data, headEmail);
          if (error || !root) {
            setParseError(error || 'Failed to parse CSV');
            setTree(null);
            return;
          }
          setTree(root);
          // Check all descendants by default
          const allIds = collectAllIds(root);
          const initialChecked: CheckedState = new Map();
          for (const id of allIds) {
            if (id !== root.PersonID) {
              initialChecked.set(id, true);
            }
          }
          setChecked(initialChecked);
        },
        error(err) {
          setParseError(`CSV parse error: ${err.message}`);
          setTree(null);
        },
      });

      // Reset file input so re-uploading the same file triggers onChange
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [headEmail],
  );

  const handleToggle = useCallback(
    (pid: number, newVal: boolean) => {
      if (!tree) return;

      // Find the node and toggle it + all descendants
      function findNode(node: TreeNode): TreeNode | null {
        if (node.PersonID === pid) return node;
        for (const child of node.children) {
          const found = findNode(child);
          if (found) return found;
        }
        return null;
      }

      const target = findNode(tree);
      if (!target) return;

      const idsToToggle = collectAllIds(target);
      setChecked((prev) => {
        const next = new Map(prev);
        for (const id of idsToToggle) {
          if (id !== tree.PersonID) {
            next.set(id, newVal);
          }
        }
        return next;
      });
    },
    [tree],
  );

  const handleGenerate = useCallback(async () => {
    if (!tree) return;
    setIsGenerating(true);
    setOutputJson(null);
    setMissingAgentIds([]);
    setLookupProgress(null);

    try {
      const emails = collectAllEmails(tree, checked);
      const { agentIds, missing } = await lookupAgentIds(emails, brand, (done, total) =>
        setLookupProgress({ done, total }),
      );
      setMissingAgentIds(missing);
      const json = generateOutputJson(tree, checked, agentIds);
      setOutputJson(json);
      setCopied(false);
    } finally {
      setIsGenerating(false);
      setLookupProgress(null);
    }
  }, [tree, checked, brand]);

  const handleCopy = useCallback(async () => {
    if (!outputJson) return;
    await navigator.clipboard.writeText(outputJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [outputJson]);

  const checkedCount = useMemo(() => {
    if (!tree) return 0;
    // Count checked among all descendants (not root)
    let count = 0;
    for (const child of tree.children) {
      count += countChecked(child, checked);
    }
    return count;
  }, [tree, checked]);

  return (
    <Container maxWidth="lg">
      <Helmet>
        <title>Update CS Agents | Admin</title>
      </Helmet>

      <PageHeader title="Update CS Agents List" />

      <Box mt={2} mb={3}>
        <Link component={RouterLink} to="/sms-verify-stats">
          Back to SMS Verification Stats
        </Link>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a BambooHR org chart CSV to generate an updated csAgents.json. The CSV is processed entirely in your
          browser -- nothing is sent to a server.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
          <TextField
            label="Head of CS email"
            value={headEmail}
            onChange={(e) => setHeadEmail(e.target.value)}
            size="small"
            sx={{ minWidth: 350 }}
          />
          <Button variant="contained" component="label">
            Upload CSV
            <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleFileUpload} />
          </Button>
        </Stack>

        {parseError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {parseError}
          </Alert>
        )}
      </Paper>

      {tree && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              People under {tree.Name} ({checkedCount} selected)
            </Typography>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={checkedCount === 0 || isGenerating}
              startIcon={isGenerating ? <CircularProgress size={16} /> : null}
            >
              {isGenerating ? 'Looking up agent IDs...' : 'Generate JSON'}
            </Button>
          </Box>

          {isGenerating && lookupProgress && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={(lookupProgress.done / lookupProgress.total) * 100} />
              <Typography variant="caption" color="text.secondary">
                Looking up agent IDs: {lookupProgress.done} / {lookupProgress.total}
              </Typography>
            </Box>
          )}

          <Box sx={{ maxHeight: 500, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
            {tree.children.map((child) => (
              <HierarchyNode key={child.PersonID} node={child} checked={checked} onToggle={handleToggle} depth={0} />
            ))}
          </Box>
        </Paper>
      )}

      {missingAgentIds.length > 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          Could not find agentId for {missingAgentIds.length} email(s): {missingAgentIds.join(', ')}
        </Alert>
      )}

      {outputJson && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Generated csAgents.json</Typography>
            <Button variant="outlined" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy to clipboard'}
            </Button>
          </Box>
          <TextField
            multiline
            fullWidth
            value={outputJson}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } } }}
            maxRows={30}
          />
        </Paper>
      )}
    </Container>
  );
}
